import { useEffect, useMemo, useRef, useState } from "react";
import Navbar from "@/components/Navbar";
import MapView from "@/components/MapView";
import RingConfigurator from "@/components/RingConfigurator";
import ElevationProfile from "@/components/ElevationProfile";
import RoadbookView from "@/components/RoadbookView";
import { extractTurns, haversineKm as haversineArr } from "@/lib/navigation";
import { api, postWithRetry } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import {
  DEFAULT_START,
  SPEEDS,
  ZONES,
  haversineKm,
} from "@/lib/dashboardHelpers";
import { useVoiceConfig } from "@/hooks/useVoiceConfig";
import { useTracking } from "@/hooks/useTracking";
import { useTrackingVoice } from "@/hooks/useTrackingVoice";
import { useGeocodeSearch } from "@/hooks/useGeocodeSearch";
import { useRecentDestinations } from "@/hooks/useRecentDestinations";
import { useRouteWeather } from "@/hooks/useRouteWeather";
import {
  Bike,
  Locate,
  Play,
  Save,
  Square,
  Compass,
  Timer,
  Route,
  Navigation,
  Download,
  Volume2,
  VolumeX,
  MapPinned,
  Users,
  Repeat,
  ArrowLeftRight,
  Share2,
  Mic,
  MicOff,
  ArrowRight,
  Loader2,
  Crosshair,
  Mountain,
  TreePine,
  Waypoints,
  Info,
} from "lucide-react";

const RIDING_OFFROAD = { turismo: 0, sterrato: 50, adventure: 35, enduro: 85 };
const ridingFromOffroad = (n) => (n < 15 ? "turismo" : n < 45 ? "adventure" : n < 70 ? "sterrato" : "enduro");

export default function Dashboard() {
  const { t, lang } = useI18n();

  // ---- Core configurator state ----
  const [vehicle, setVehicle] = useState("moto");
  const [paramMode, setParamMode] = useState("distance"); // distance | time
  const [distance, setDistance] = useState(80);
  const [timeH, setTimeH] = useState(2);
  const [offroad, setOffroad] = useState(35);
  const [terrain, setTerrain] = useState("adventure");

  const [start, setStart] = useState(DEFAULT_START);
  const [startFromGps, setStartFromGps] = useState(false);
  // Frozen start: locked coords after first Generate to keep loops deterministic
  const [frozenStart, setFrozenStart] = useState(null);
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(false);

  // Altimetry self-heal: if a route (cached / shared / legacy) arrives
  // without an elevation_profile, fetch it on demand so the ALTIMETRIA
  // strip is always available during navigation. D+/D− get replaced with
  // the hysteresis-filtered values so they match the displayed profile.
  useEffect(() => {
    if (!route?.polyline || route.polyline.length < 2) return;
    if (route.elevation_profile && route.elevation_profile.length > 1) return;
    let cancelled = false;
    (async () => {
      try {
        const pl = route.polyline;
        const step = Math.max(1, Math.ceil(pl.length / 400));
        const sampled = pl.filter((_, i) => i % step === 0);
        const last = pl[pl.length - 1];
        if (sampled[sampled.length - 1] !== last) sampled.push(last);
        const { data } = await api.post("/routes/elevation", { polyline: sampled });
        if (cancelled || !data?.profile || data.profile.length < 2) return;
        setRoute((r) =>
          r
            ? {
                ...r,
                elevation_profile: data.profile,
                elevation_gain_m: data.elevation_gain_m,
                elevation_loss_m: data.elevation_loss_m,
                min_elev_m: data.min_elev_m,
                max_elev_m: data.max_elev_m,
              }
            : r
        );
      } catch {
        /* silent — strip simply stays hidden if the provider is down */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route?.route_id]);
  const [saving, setSaving] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [gpxDownloading, setGpxDownloading] = useState(false);

  // Which tool panel is active in the config: loop | destination | community | andata-ritorno
  const [activeTool, setActiveTool] = useState("loop");

  // Destination-mode state
  const [dest, setDest] = useState(null);
  const [destSearchQ, setDestSearchQ] = useState("");

  // Andata-Ritorno turn-point (optional)
  const [arTurnQ, setArTurnQ] = useState("");
  const [arTurn, setArTurn] = useState(null);

  // Community-mode state
  const [zoneId, setZoneId] = useState(null);
  const [communityRides, setCommunityRides] = useState([]);
  const [communityLoading, setCommunityLoading] = useState(false);

  // Tracking-related toggles owned by Dashboard (persist across stop/start)
  const [compassMode, setCompassMode] = useState(true);
  const [voiceOn, setVoiceOn] = useState(true);
  // View toggle: "map" (default) or "roadbook" (rally tulip cards).
  // Auto-forced to "map" when tracking starts so the rider always sees GPS.
  const [visualView, setVisualView] = useState("map");

  // Manual pan handling: when the rider drags the map to peek ahead we
  // pause auto-follow. `userPanned=true` shows the RICENTRA button. The
  // 15-second auto-resume timer is stored in a ref so we can reset it
  // whenever the user drags again.
  const [userPanned, setUserPanned] = useState(false);
  const panResumeTimerRef = useRef(null);
  const requestRecenter = () => {
    setUserPanned(true);
    if (panResumeTimerRef.current) clearTimeout(panResumeTimerRef.current);
    panResumeTimerRef.current = setTimeout(() => setUserPanned(false), 15000);
  };
  const dismissPan = () => {
    if (panResumeTimerRef.current) clearTimeout(panResumeTimerRef.current);
    panResumeTimerRef.current = null;
    setUserPanned(false);
  };
  useEffect(() => () => {
    if (panResumeTimerRef.current) clearTimeout(panResumeTimerRef.current);
  }, []);

  // ---- Backend-proxied geocode searches (destination + AR turn point) ----
  const { results: destSearchResults, searching: destSearching, error: destSearchError } = useGeocodeSearch(
    destSearchQ,
    activeTool === "destination",
    start,
  );
  const { results: arTurnResults, searching: arTurnSearching, error: arTurnError } = useGeocodeSearch(
    arTurnQ,
    activeTool === "andata-ritorno",
    start,
  );

  // ---- Recent destinations (localStorage — last 5) ----
  const { recents: recentDests, push: pushRecentDest, clear: clearRecents } = useRecentDestinations();

  // Phase 4: prefer GraphHopper NATIVE turn-by-turn instructions when
  // provided by the backend (graph-aware — no more false-turn heuristics).
  // We map each GH instruction to the same shape our tracking/roadbook
  // components expect: {index, lat, lng, direction, delta, cumKm, text}.
  // - We keep ONLY entries where `is_bifurcation` is true (real turns /
  //   roundabouts / U-turns) — CONTINUE and KEEP-*, ARRIVE are silent.
  // - `extractTurns()` is retained as a fallback for legacy providers
  //   (ors / osrm / synthetic) that don't return instructions.
  const turnPoints = useMemo(() => {
    if (!route?.polyline) return [];
    const gh = route.instructions;
    if (Array.isArray(gh) && gh.length && route.provider === "graphhopper") {
      // Compute cumulative km per polyline vertex once so we can label each
      // turn card in RoadbookView.
      const poly = route.polyline;
      const cum = [0];
      for (let i = 1; i < poly.length; i++) {
        cum.push(cum[i - 1] + haversineArr(poly[i - 1], poly[i]));
      }
      const out = [];
      for (const ins of gh) {
        if (!ins.is_bifurcation) continue;
        const idx = Array.isArray(ins.interval) ? Math.max(0, Math.min(poly.length - 1, ins.interval[0])) : 0;
        if (!poly[idx]) continue;
        // Map GH `direction` (left/right/u-turn/roundabout/keep-*) to the
        // legacy schema (left/right/u-turn). Roundabout renders as sharp turn.
        let direction = ins.direction;
        if (direction === "roundabout") direction = "right";
        else if (direction === "keep-left") direction = "left";
        else if (direction === "keep-right") direction = "right";
        // Signed delta only used for the tulip icon rendering.
        // sign 2/3 → +90/+135 (right), -2/-3 → -90/-135 (left), ±8 → ±180 (u-turn).
        let delta = 0;
        const s = ins.sign;
        if (s === 1) delta = 30;
        else if (s === 2) delta = 90;
        else if (s === 3) delta = 135;
        else if (s === -1) delta = -30;
        else if (s === -2) delta = -90;
        else if (s === -3) delta = -135;
        else if (s === 8) delta = 180;
        else if (s === -8 || s === -98) delta = -180;
        else if (s === 6) delta = 90; // roundabout ~ right turn
        out.push({
          index: idx,
          lat: poly[idx][0],
          lng: poly[idx][1],
          direction,
          delta,
          cumKm: cum[idx],
          text: ins.text,
          street_name: ins.street_name,
          sign: ins.sign,
        });
      }
      return out;
    }
    // Legacy fallback: infer turns from polyline geometry heuristically.
    return extractTurns(route.polyline, { minAngle: 60, minSegmentM: 80, smoothSpanM: 200, smoothMaxAngle: 45 });
  }, [route?.polyline, route?.instructions, route?.provider]);

  // Weather forecast along the polyline (5 sampled points)
  const { weatherPoints } = useRouteWeather(route?.polyline, { enabled: !!route?.polyline });

  // TTS helper — used by tracking hook + a few one-off cues from the parent
  const speak = (text) => {
    if (!voiceOn) return;
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.lang = lang === "en" ? "en-US" : "it-IT";
      u.rate = 1.0;
      u.pitch = 1.0;
      u.volume = 1.0;
      const voices = window.speechSynthesis.getVoices();
      const pref = voices.find((v) => v.lang && v.lang.startsWith(lang === "en" ? "en" : "it"));
      if (pref) u.voice = pref;
      window.speechSynthesis.speak(u);
    } catch { /* best-effort */ }
  };

  // ---- Derived: effective distance / duration / signature ----
  const effectiveDistance = useMemo(() => {
    if (paramMode === "time") {
      return Math.max(2, Math.round(timeH * SPEEDS[vehicle] * (1 - offroad / 200)));
    }
    return distance;
  }, [paramMode, timeH, distance, vehicle, offroad]);

  const durationEst = useMemo(() => {
    const avg = SPEEDS[vehicle] * (1 - offroad / 200);
    return Math.max(1, Math.round((effectiveDistance / Math.max(avg, 3)) * 60));
  }, [effectiveDistance, vehicle, offroad]);

  const currentSig = useMemo(
    () =>
      `${activeTool}|${vehicle}|${effectiveDistance}|${offroad}|${start.lat.toFixed(3)}|${start.lng.toFixed(3)}|${dest ? dest.lat.toFixed(3) + "," + dest.lng.toFixed(3) : "no-dest"}|${arTurn ? arTurn.lat.toFixed(3) + "," + arTurn.lng.toFixed(3) : "no-ar"}`,
    [activeTool, vehicle, effectiveDistance, offroad, start, dest, arTurn]
  );

  const routeInSync = route && route._sig === currentSig;
  const generatedKm = route?.distance_km;
  const displayDistance = routeInSync && generatedKm != null ? generatedKm : effectiveDistance;
  const displayDuration = routeInSync && route?.duration_min != null ? route.duration_min : durationEst;

  // ---------- Handlers ----------
  const applyDestPick = (name, lat, lng) => {
    const pick = { name, lat, lng };
    setDest(pick);
    setDestSearchQ("");
    pushRecentDest(pick);
  };

  const generate = async () => {
    if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
      try { navigator.vibrate(20); } catch { /* ignored */ }
    }
    setLoading(true);
    const effectiveStart = frozenStart || start;
    try {
      const isDestination = activeTool === "destination";
      const isAndataRitorno = activeTool === "andata-ritorno";
      if (isDestination && !dest) {
        toast.error(t("selectDestFirst"));
        setLoading(false);
        return;
      }
      const body = {
        vehicle,
        distance_km: isDestination
          ? Math.max(2, Math.round(haversineKm(effectiveStart, dest) * 1.3))
          : effectiveDistance,
        offroad_percent: offroad,
        riding_profile: terrain,
        start_lat: effectiveStart.lat,
        start_lng: effectiveStart.lng,
        start_label: effectiveStart.name,
        route_type: isDestination ? "destination" : isAndataRitorno ? "andata-ritorno" : "loop",
      };
      if (isDestination) {
        body.dest_lat = dest.lat;
        body.dest_lng = dest.lng;
        body.dest_label = dest.name;
      }
      if (isAndataRitorno && arTurn) {
        body.dest_lat = arTurn.lat;
        body.dest_lng = arTurn.lng;
        body.dest_label = arTurn.name;
      }
      const { data } = await postWithRetry("/routes/generate", body, { attempts: 3, delayMs: 1500 });
      // Phase 5.12: preserve the destination context on the client-side
      // `route` object so the off-route deviation endpoint can plan a
      // full re-route to the same target. Backend doesn't echo these
      // fields, so we copy them from the request body here.
      setRoute({
        ...data,
        _sig: currentSig,
        route_type: body.route_type,
        dest_lat: body.dest_lat,
        dest_lng: body.dest_lng,
        offroad_percent: body.offroad_percent,
      });
      if (!frozenStart) setFrozenStart(effectiveStart);
      toast.success(t("routeGenerated"), {
        description: `${data.distance_km} km · ${data.difficulty}`,
      });
    } catch (e) {
      const status = e?.response?.status;
      const isNetwork = !e?.response || e?.code === "ECONNABORTED" || e?.code === "ERR_NETWORK";
      if (isNetwork || status >= 500) {
        toast.error("Connessione debole", { description: "Verifica il segnale e riprova" });
      } else {
        const detail = e?.response?.data?.detail;
        const detailMsg =
          typeof detail === "string"
            ? detail
            : detail && typeof detail === "object"
            ? (lang === "en" ? detail.message_en : detail.message_it) || detail.message_it || "Riprova"
            : "Riprova";
        toast.error("Errore nella generazione", { description: detailMsg });
      }
    } finally {
      setLoading(false);
    }
  };

  const saveRoute = async () => {
    if (!route) return;
    setSaving(true);
    try {
      const name = `${start.name} · ${route.distance_km}km · ${route.difficulty}`;
      // eslint-disable-next-line no-unused-vars
      const { _sig, ...routePayload } = route;
      await api.post("/routes", { name, route: routePayload });
      toast.success("Salvato nella tua cronologia");
    } catch (e) {
      toast.error("Impossibile salvare", { description: e?.response?.data?.detail || "Riprova" });
    } finally {
      setSaving(false);
    }
  };

  const shareRoute = async () => {
    if (!route) return;
    setSharing(true);
    try {
      // eslint-disable-next-line no-unused-vars
      const { _sig, ...routePayload } = route;
      const name = `${start.name} · ${route.distance_km}km · ${route.difficulty}`;
      const { data } = await api.post("/routes/share", { name, route: routePayload });
      // Use the backend /api/share/{share_id} preview URL so WhatsApp / Facebook
      // / Telegram show a rich OG preview (title, description, Brema image).
      // The backend page immediately redirects real user browsers to /r/{share_id}.
      const shareUrl = `${window.location.origin}/api/share/${data.share_id}`;
      const text = lang === "en"
        ? `Check out the off-road route I built on ValliRider! ${shareUrl}`
        : `Guarda il percorso fuoristrada che ho creato su ValliRider! ${shareUrl}`;
      if (navigator.share) {
        try {
          await navigator.share({
            title: "ValliRider · Percorso condiviso",
            text,
            url: shareUrl,
          });
          toast.success(lang === "en" ? "Shared" : "Condiviso");
          return;
        } catch (err) {
          if (err?.name === "AbortError") return;
        }
      }
      const waUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`;
      window.open(waUrl, "_blank", "noopener,noreferrer");
      toast.success(lang === "en" ? "Link created — share on WhatsApp" : "Link creato — condividi su WhatsApp");
    } catch (e) {
      toast.error(lang === "en" ? "Couldn't create link" : "Impossibile creare il link", { description: e?.response?.data?.detail || "Riprova" });
    } finally {
      setSharing(false);
    }
  };

  const locate = () => {
    if (!navigator.geolocation) return toast.error(t("geoUnavailable"));
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setStart({
          name: lang === "en" ? "GPS Position" : "Posizione GPS",
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
        setStartFromGps(true);
        toast.success(t("geoAcquired"));
      },
      () => {
        setStartFromGps(false);
        toast.error(t("geoDenied"));
      }
    );
  };

  const toggleGps = () => {
    if (startFromGps) {
      setStartFromGps(false);
      toast(t("gpsDeactivated"), { icon: "⏻" });
    } else {
      locate();
    }
  };

  const downloadGpx = async () => {
    if (!route?.polyline?.length) return toast.error("Nessun percorso generato");
    setGpxDownloading(true);
    try {
      const res = await api.post(
        "/routes/export/gpx",
        {
          name: `ValliRider ${route.distance_km}km ${route.vehicle}`,
          vehicle: route.vehicle,
          polyline: route.polyline,
        },
        { responseType: "blob" }
      );
      const filename = `vallirider-${route.distance_km}km-${route.vehicle}.gpx`;
      const file = new File([res.data], filename, { type: "application/gpx+xml" });

      if (
        navigator.share &&
        typeof navigator.canShare === "function" &&
        navigator.canShare({ files: [file] })
      ) {
        try {
          await navigator.share({
            files: [file],
            title: "ValliRider GPX",
            text: `Percorso ${route.distance_km} km · ${route.vehicle}`,
          });
          toast.success("Condiviso");
        } catch (err) {
          if (err?.name !== "AbortError") _fallbackDownload(res.data, filename);
        }
      } else {
        _fallbackDownload(res.data, filename);
      }
    } catch {
      toast.error("Errore nel download del GPX");
    } finally {
      setGpxDownloading(false);
    }
  };

  const _fallbackDownload = (blob, filename) => {
    const b = new Blob([blob], { type: "application/gpx+xml" });
    const url = URL.createObjectURL(b);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("GPX scaricato");
  };

  // ---------- Voice command: apply parsed slot values to UI state ----------
  const geocodeDestinationText = async (text) => {
    // Phase 5.7: route through the backend /api/geocode proxy — the
    // direct Nominatim call from the browser was rejected with 403 due
    // to the User-Agent policy, which silently killed voice-destination
    // lookups. Same endpoint the typed destination search uses.
    try {
      const { data } = await api.get("/geocode", {
        params: { q: text, lat: start.lat, lng: start.lng, limit: 1 },
      });
      if (Array.isArray(data) && data[0]) {
        return {
          name: data[0].display_name.split(",").slice(0, 2).join(", "),
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon),
        };
      }
    } catch { /* ignored — caller shows a toast on null */ }
    return null;
  };

  const applyVoiceParsed = async (parsed) => {
    if (!parsed || Object.keys(parsed).length === 0) return false;
    if (parsed.vehicle === "moto" || parsed.vehicle === "ebike") setVehicle(parsed.vehicle);
    if (["loop", "destination", "community", "andata-ritorno"].includes(parsed.activeTool)) setActiveTool(parsed.activeTool);
    if (parsed.paramMode === "distance" || parsed.paramMode === "time") setParamMode(parsed.paramMode);
    if (typeof parsed.distance === "number") setDistance(Math.max(5, Math.min(200, Math.round(parsed.distance))));
    if (typeof parsed.timeH === "number") {
      setTimeH(Math.max(0.5, Math.min(8, parsed.timeH)));
      setParamMode("time");
    }
    if (typeof parsed.offroad === "number") {
      const tKey = ridingFromOffroad(Math.max(0, Math.min(100, Math.round(parsed.offroad))));
      setTerrain(tKey);
      setOffroad(RIDING_OFFROAD[tKey]);
    }
    if (parsed.destination) {
      setActiveTool("destination");
      const geo = await geocodeDestinationText(parsed.destination);
      if (geo) {
        setDest(geo);
        parsed.__destResolved = geo.name;
      } else {
        toast.error(`Non ho trovato "${parsed.destination}"`);
      }
    }
    return true;
  };

  const nextMissingSlot = (acc) => {
    const mode = acc.activeTool || activeTool;
    if (mode === "destination") {
      if (!acc.destination && !dest) return "destination";
      return null;
    }
    if (mode === "loop" || !mode) {
      const hasDistance = typeof acc.distance === "number" || acc.paramMode === "distance";
      const hasTime = typeof acc.timeH === "number" || acc.paramMode === "time";
      if (!hasDistance && !hasTime) return "distance";
      return null;
    }
    return null;
  };

  const {
    voiceStage,
    voiceTranscript,
    voiceHint,
    startVoiceCommand,
  } = useVoiceConfig({
    lang,
    t,
    applyParsed: applyVoiceParsed,
    nextMissingSlot,
    onConfirm: () => generate(),
    externalGeneratingFlag: loading,
  });

  // ---- Live GPS tracking ----
  // Stable polyline reference: memoize so MapView never sees the prop as
  // "changed" when only sibling state (tracking stats, live coords) ticks.
  // The identity of route.polyline is already stable across re-renders
  // (it changes only on setRoute), but this makes the contract explicit
  // and cheap to reason about.
  const routePolyline = useMemo(() => route?.polyline ?? null, [route?.polyline]);

  const {
    tracking,
    live,
    trackStats,
    movingNow,
    nearTurn,
    snapHeading,
    speedKmh,
    currentTurnIdx,
    routeProgressIdx,
    startTracking,
    stopTracking,
  } = useTracking({
    route,
    turnPoints,
    vehicle,
    lang,
    voiceOn,
    speak,
    onStop: () => {
      // STOP AND RESET: fully clear active route + force next search to
      // start from vehicle's CURRENT GPS position.
      dismissPan();
      const lastPos = live && live.lat != null && live.lng != null ? { lat: live.lat, lng: live.lng } : null;
      setRoute(null);
      setDest(null);
      setDestSearchQ("");
      setArTurn(null);
      setArTurnQ("");
      if (lastPos) {
        setStart({
          name: lang === "en" ? "GPS Position" : "Posizione GPS",
          lat: lastPos.lat,
          lng: lastPos.lng,
        });
        setStartFromGps(true);
      }
    },
  });

  // Ridden portion of the route line "erases" as the rider passes it: only
  // while tracking, trim the master polyline down to what's still ahead of
  // routeProgressIdx (a forward-only vertex index — see useTracking). The
  // untrimmed `routePolyline` is left untouched since off-route detection,
  // rerouting and turn extraction all still need the FULL original route.
  const visiblePolyline = useMemo(() => {
    if (!tracking || !routePolyline) return routePolyline;
    return routePolyline.slice(routeProgressIdx);
  }, [routePolyline, tracking, routeProgressIdx]);

  // ---------- Voice commands during tracking: DISABLED ----------
  // Track Following mode: no always-on microphone during the ride.
  // The on-screen STOP button is the only way to end the session.
  useTrackingVoice({
    enabled: false,
    lang,
    handlers: {
      onStop: () => {
        stopTracking('user');
      },
      onPause: () => {},
      onResume: () => {},
      onVoiceOff: () => {
        setVoiceOn(false);
        if (typeof window !== "undefined" && window.speechSynthesis) {
          window.speechSynthesis.cancel();
        }
      },
      onVoiceOn: () => {
        setVoiceOn(true);
      },
    },
  });

  // ---------- Community: load rides for selected zone ----------
  useEffect(() => {
    if (activeTool !== "community" || !zoneId) {
      setCommunityRides([]);
      return;
    }
    const zone = ZONES.find((z) => z.id === zoneId);
    if (!zone) return;
    setCommunityLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/routes");
        const filtered = (data || []).filter((r) => {
          const d = haversineKm(zone, { lat: r.start_lat, lng: r.start_lng });
          return d <= zone.radius;
        });
        setCommunityRides(filtered);
      } catch {
        setCommunityRides([]);
      } finally {
        setCommunityLoading(false);
      }
    })();
  }, [activeTool, zoneId]);

  const loadCommunityRide = (r) => {
    const routeShape = {
      route_id: r.route_id,
      vehicle: r.vehicle,
      distance_km: r.distance_km,
      offroad_percent: r.offroad_percent,
      duration_min: r.duration_min,
      elevation_gain_m: r.elevation_gain_m,
      elevation_loss_m: r.elevation_loss_m ?? null,
      min_elev_m: r.min_elev_m ?? null,
      max_elev_m: r.max_elev_m ?? null,
      elevation_profile: r.elevation_profile ?? null,
      difficulty: r.difficulty,
      route_type: r.route_type || "loop",
      direction: r.direction ?? null,
      start_lat: r.start_lat,
      start_lng: r.start_lng,
      start_label: r.start_label,
      polyline: r.polyline,
      waypoints: r.waypoints || [],
      _sig: `preset-${r.route_id}`,
    };
    setStart({ name: r.start_label, lat: r.start_lat, lng: r.start_lng });
    setRoute(routeShape);
    toast.success(`Caricato: ${r.name || r.start_label}`);
  };

  // Unfreeze start on any explicit reconfiguration
  useEffect(() => {
    setFrozenStart(null);
  }, [vehicle, activeTool, effectiveDistance, offroad, start.name, start.lat, start.lng, dest?.lat, dest?.lng]);

  // Auto-regenerate on config change (debounced) — only for loop / A-R
  const [firstMount, setFirstMount] = useState(true);
  useEffect(() => {
    if (firstMount) {
      setFirstMount(false);
      return;
    }
    if (activeTool !== "loop" && activeTool !== "andata-ritorno") return;
    const timer = setTimeout(() => generate(), 600);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTool,
    start.lat,
    start.lng,
    vehicle,
    effectiveDistance,
    offroad,
    arTurn?.lat,
    arTurn?.lng,
  ]);

  // First-mount route generation
  useEffect(() => {
    generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Force map view when tracking starts (rider needs GPS visibility)
  useEffect(() => {
    if (tracking) setVisualView("map");
  }, [tracking]);

  // ==================== RENDER ====================
  return (
    <div className="min-h-screen text-white">
      {/* FULLSCREEN TRACKING OVERLAY — covers everything while riding so the
          rider only sees the map, the turn card, and the stop button. */}
      {tracking && (
        <div
          data-testid="fullscreen-tracking"
          className="fixed inset-0 z-[9998] bg-brema-obsidian flex flex-col"
        >
          {/* Top bar: turn banner slot (rendered by MapView) + stop button */}
          <div className="absolute top-0 left-0 right-0 z-[9999] px-3 pt-3 pointer-events-none">
            <div className="flex items-start gap-2 pointer-events-auto">
              <button
                data-testid="fullscreen-stop-btn"
                onClick={() => stopTracking('user')}
                className="flex-shrink-0 bg-brema-red text-white font-display font-black uppercase tracking-widest px-4 py-3 border-2 border-white shadow-2xl flex items-center gap-2 text-sm active:scale-95 transition-transform"
                aria-label={lang === "en" ? "Stop" : "Stop"}
              >
                <Square size={16} />
                STOP
              </button>
              <div className="flex-1" />
              {/* View toggle top-right */}
              {route && (
                <div
                  data-testid="view-toggle-fs"
                  className="flex-shrink-0 bg-brema-obsidian/90 border border-white/25 backdrop-blur flex shadow-2xl"
                >
                  <button
                    data-testid="view-toggle-fs-map"
                    onClick={() => setVisualView("map")}
                    className={`px-3 py-2 font-mono uppercase tracking-widest text-[10px] transition ${
                      visualView === "map"
                        ? "bg-brema-yellow text-brema-obsidian"
                        : "text-brema-yellow"
                    }`}
                  >
                    {lang === "en" ? "Map" : "Mappa"}
                  </button>
                  <button
                    data-testid="view-toggle-fs-roadbook"
                    onClick={() => setVisualView("roadbook")}
                    className={`px-3 py-2 font-mono uppercase tracking-widest text-[10px] transition ${
                      visualView === "roadbook"
                        ? "bg-brema-yellow text-brema-obsidian"
                        : "text-brema-yellow"
                    }`}
                  >
                    Roadbook
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Map or Roadbook driving card — fills the whole viewport */}
          <div className="absolute inset-0">
            {visualView === "roadbook" && route ? (
              <RoadbookView
                turns={turnPoints}
                totalKm={route?.distance_km || 0}
                driving={tracking}
                currentTurnIdx={currentTurnIdx}
                nearTurn={nearTurn}
              />
            ) : (
              <MapView
                start={start}
                polyline={visiblePolyline}
                live={live}
                followUser={!userPanned}
                compassMode={compassMode}
                nearTurn={nearTurn}
                snapHeading={snapHeading}
                rejoinPolyline={null}
                weatherPoints={weatherPoints}
                speedKmh={speedKmh}
                onRequestRecenter={requestRecenter}
              />
            )}
          </div>

          {/* RICENTRA button — appears when the user has dragged the map
              manually. Tapping it (or 15s of inactivity) re-enables the
              auto-follow. Positioned centre-right for thumb reach. */}
          {userPanned && (
            <button
              data-testid="recenter-btn"
              onClick={dismissPan}
              className="pointer-events-auto absolute right-3 top-1/2 -translate-y-1/2 z-[9999] bg-brema-yellow text-brema-obsidian font-display font-black uppercase tracking-widest px-4 py-3 border-2 border-brema-obsidian shadow-2xl flex items-center gap-2 text-sm active:scale-95 transition-transform"
              aria-label="Ricentra"
            >
              <Crosshair size={18} />
              {lang === "en" ? "RECENTER" : "RICENTRA"}
            </button>
          )}

          {/* Bottom bar: elevation mini-strip + stats + compass + voice toggles */}
          <div className="absolute bottom-0 left-0 right-0 z-[9999] px-3 pb-3 space-y-2 pointer-events-none">
            {/* Elevation mini-strip: compact horizontal profile with a
                yellow marker that tracks the rider's progress along the
                route. Restored per user directive after road test.       */}
            {route?.elevation_profile?.length > 1 && (
              <div
                data-testid="fs-elevation-strip"
                className="pointer-events-auto bg-brema-obsidian/90 border border-white/15 backdrop-blur px-3 py-2 shadow-2xl"
              >
                <div className="flex items-center justify-between mb-1 font-mono text-[9px] uppercase tracking-widest text-zinc-400">
                  <span>{lang === "en" ? "Elevation" : "Altimetria"}</span>
                  <span className="text-brema-yellow tabular-nums">
                    ↗ +{route.elevation_gain_m || 0}m · ↘ -{route.elevation_loss_m || 0}m
                  </span>
                </div>
                <ElevationMiniStrip
                  profile={route.elevation_profile}
                  minM={route.min_elev_m}
                  maxM={route.max_elev_m}
                  progressKm={trackStats.km}
                  totalKm={route.distance_km}
                />
              </div>
            )}
            <div className="grid grid-cols-3 gap-2 pointer-events-auto">
              {/* KM MANCANTI — dato principale, molto grande */}
              <div
                data-testid="stat-remaining"
                className="col-span-1 bg-brema-yellow text-brema-obsidian border-2 border-brema-obsidian px-3 py-2 shadow-2xl"
              >
                <div className="font-mono text-[9px] font-bold uppercase tracking-widest opacity-80">
                  {lang === "en" ? "Remaining" : "Mancanti"}
                </div>
                <div className="font-display font-black leading-none tabular-nums" style={{ fontSize: "2rem" }}>
                  {Math.max(0, (route?.distance_km || 0) - trackStats.km).toFixed(1)}
                  <span className="ml-1 text-xs font-mono">KM</span>
                </div>
              </div>
              {/* KM PERCORSI + ETA — dati secondari */}
              <div
                data-testid="stat-driven"
                className="bg-brema-obsidian/90 border border-white/20 backdrop-blur px-3 py-2 shadow-2xl"
              >
                <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-400">
                  {lang === "en" ? "Driven" : "Percorsi"}
                </div>
                <div className="font-display font-bold text-lg leading-none text-brema-red tabular-nums">
                  {trackStats.km.toFixed(1)}
                  <span className="ml-1 text-[10px] font-mono text-zinc-400">KM</span>
                </div>
                <div className="mt-0.5 font-mono text-[9px] uppercase text-zinc-500 tabular-nums">
                  {Math.floor(trackStats.seconds / 60)}:{String(trackStats.seconds % 60).padStart(2, "0")}
                </div>
              </div>
              <div
                data-testid="stat-eta"
                className="bg-brema-obsidian/90 border border-white/20 backdrop-blur px-3 py-2 shadow-2xl"
              >
                <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-400">
                  {lang === "en" ? "ETA" : "Arrivo"}
                </div>
                {(() => {
                  // ETA = tempo previsto rimanente basato sulla velocità media
                  // corrente (o durata originale se ancora a 0 km).
                  const totalKm = route?.distance_km || 0;
                  const remainKm = Math.max(0, totalKm - trackStats.km);
                  let etaMin = null;
                  if (trackStats.km > 0.1 && trackStats.seconds > 20) {
                    const avgKmh = (trackStats.km / trackStats.seconds) * 3600;
                    if (avgKmh > 3) etaMin = Math.round((remainKm / avgKmh) * 60);
                  } else if (route?.duration_min && totalKm > 0) {
                    etaMin = Math.round((remainKm / totalKm) * route.duration_min);
                  }
                  if (etaMin == null) {
                    return <div className="font-display font-bold text-lg leading-none text-zinc-500">—</div>;
                  }
                  const hh = Math.floor(etaMin / 60);
                  const mm = etaMin % 60;
                  return (
                    <div className="font-display font-bold text-lg leading-none text-white tabular-nums">
                      {hh > 0 ? `${hh}h ` : ""}{mm}<span className="ml-1 text-[10px] font-mono text-zinc-400">MIN</span>
                    </div>
                  );
                })()}
                <div className="mt-0.5 font-mono text-[9px] uppercase text-zinc-500 tabular-nums">
                  {movingNow ? (lang === "en" ? "moving" : "in marcia") : (lang === "en" ? "paused" : "in pausa")}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 pointer-events-auto">
              <button
                onClick={() => setCompassMode((v) => !v)}
                className={`px-3 py-2 border font-mono uppercase tracking-widest text-[11px] flex items-center justify-center gap-2 shadow-2xl backdrop-blur ${
                  compassMode
                    ? "bg-brema-yellow text-brema-obsidian border-brema-yellow"
                    : "bg-brema-obsidian/90 border-brema-yellow/40 text-brema-yellow"
                }`}
              >
                <Compass size={12} />
                {compassMode ? "COMPASS ON" : "COMPASS OFF"}
              </button>
              <button
                onClick={() => {
                  const next = !voiceOn;
                  setVoiceOn(next);
                  if (!next && typeof window !== "undefined" && window.speechSynthesis) {
                    window.speechSynthesis.cancel();
                  }
                }}
                className={`px-3 py-2 border font-mono uppercase tracking-widest text-[11px] flex items-center justify-center gap-2 shadow-2xl backdrop-blur ${
                  voiceOn
                    ? "bg-brema-yellow text-brema-obsidian border-brema-yellow"
                    : "bg-brema-obsidian/90 border-brema-yellow/40 text-brema-yellow"
                }`}
              >
                {voiceOn ? <Volume2 size={12} /> : <VolumeX size={12} />}
                {voiceOn ? (lang === "en" ? "VOICE ON" : "VOCE ON") : (lang === "en" ? "VOICE OFF" : "VOCE OFF")}
              </button>
            </div>
          </div>
        </div>
      )}

      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-8 grid lg:grid-cols-[420px_1fr] gap-6">
        <div className="space-y-6">
          <div className="relative bg-brema-coal border border-white/10 p-6">
            <span className="corner-tick tl" /><span className="corner-tick tr" />
            <span className="corner-tick bl" /><span className="corner-tick br" />
            <div className="font-mono text-[10px] text-brema-yellow uppercase tracking-[0.3em] mb-1">
              // Configurator
            </div>
            <h1 className="font-display font-black uppercase text-3xl tracking-tight">
              Design your route
            </h1>

            {/* 1) Mezzo */}
            <div className="mt-5">
              <FieldLabel>{t("vehicle")}</FieldLabel>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <VehicleBtn testId="vehicle-moto-btn" active={vehicle === "moto"} onClick={() => setVehicle("moto")} icon={<Bike size={16} />} label="Moto" />
                <VehicleBtn testId="vehicle-ebike-btn" active={vehicle === "ebike"} onClick={() => setVehicle("ebike")} icon={<Bike size={16} />} label="Bike / E-Bike" />
              </div>
            </div>

            {/* 2) Modalità */}
            <div className="mt-5">
              <FieldLabel>{t("mode")}</FieldLabel>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <ToolBtn testId="tool-loop-btn" active={activeTool === "loop"} onClick={() => setActiveTool("loop")} icon={<Repeat size={14} />} label={t("loop")} />
                <ToolBtn testId="tool-andata-ritorno-btn" active={activeTool === "andata-ritorno"} onClick={() => setActiveTool("andata-ritorno")} icon={<ArrowLeftRight size={14} />} label={t("andataRitorno")} />
                <ToolBtn testId="tool-destination-btn" active={activeTool === "destination"} onClick={() => setActiveTool("destination")} icon={<MapPinned size={14} />} label={t("destination")} />
                <ToolBtn testId="tool-community-btn" active={activeTool === "community"} onClick={() => setActiveTool("community")} icon={<Users size={14} />} label={t("community")} />
              </div>
            </div>

            {/* ATTIVA GPS */}
            <button
              onClick={toggleGps}
              data-testid="attiva-gps-btn"
              title={t("gpsTitle")}
              className={`mt-3 w-full border font-display font-bold uppercase tracking-widest px-4 py-3 flex items-center justify-center gap-2 transition-colors text-sm ${
                startFromGps
                  ? "border-brema-yellow bg-brema-yellow text-black"
                  : "border-brema-yellow text-brema-yellow hover:bg-brema-yellow/10"
              }`}
            >
              <Locate size={16} />
              {startFromGps ? t("gpsActive") : t("activateGps")}
            </button>

            {/* Loop / Andata-Ritorno panel */}
            {(activeTool === "loop" || activeTool === "andata-ritorno") && (
              <div className="mt-4" data-testid={activeTool === "andata-ritorno" ? "andata-ritorno-panel" : "loop-panel"}>
                {activeTool === "andata-ritorno" && (
                  <>
                    <div className="mb-3 font-mono text-[10px] text-zinc-300 leading-relaxed">
                      {t("andataRitornoDesc")}
                    </div>
                    <FieldLabel>{t("turnLocation")} <span className="text-zinc-400 lowercase tracking-normal">({t("optional")})</span></FieldLabel>
                    <div className="mt-2 relative">
                      <input
                        data-testid="ar-turn-input"
                        type="text"
                        value={arTurnQ}
                        onChange={(e) => { setArTurnQ(e.target.value); if (!e.target.value) setArTurn(null); }}
                        placeholder={t("turnPlaceholder")}
                        className="w-full bg-brema-obsidian border border-white/10 px-3 py-2 text-sm font-mono text-white focus:border-brema-yellow outline-none"
                      />
                      {arTurnQ.length >= 2 && !arTurn && (
                        <div data-testid="ar-turn-dropdown" className="mt-1 border border-white/10 bg-brema-obsidian max-h-56 overflow-y-auto">
                          {arTurnSearching && (
                            <div className="px-3 py-2 font-mono text-[10px] text-zinc-400 uppercase tracking-widest">{t("searching")}</div>
                          )}
                          {!arTurnSearching && arTurnError && (
                            <div className="px-3 py-2 font-mono text-[10px] text-brema-red uppercase tracking-widest">{t("searchUnavailable")}</div>
                          )}
                          {!arTurnSearching && !arTurnError && arTurnResults.length === 0 && (
                            <div className="px-3 py-2 font-mono text-[10px] text-zinc-400 uppercase tracking-widest">{t("noResults")}</div>
                          )}
                          {!arTurnSearching &&
                            arTurnResults.map((r) => (
                              <button
                                key={r.place_id}
                                data-testid={`ar-turn-result-${r.place_id}`}
                                onClick={() => {
                                  const shortName = r.display_name.split(",").slice(0, 2).join(", ");
                                  setArTurn({ name: shortName, lat: parseFloat(r.lat), lng: parseFloat(r.lon) });
                                  setArTurnQ(shortName);
                                }}
                                className="w-full text-left px-3 py-2 text-xs text-zinc-100 hover:bg-brema-yellow/10 hover:text-brema-yellow border-b border-white/5 last:border-b-0"
                              >
                                {r.display_name}
                              </button>
                            ))}
                        </div>
                      )}
                    </div>
                    {arTurn && (
                      <div className="mt-2 flex items-center justify-between font-mono text-[10px] uppercase tracking-widest">
                        <span className="text-brema-yellow truncate" data-testid="ar-turn-selected">→ {arTurn.name}</span>
                        <button
                          onClick={() => { setArTurn(null); setArTurnQ(""); }}
                          data-testid="ar-turn-clear"
                          className="ml-2 text-zinc-400 hover:text-brema-red border border-white/10 hover:border-brema-red/60 px-2 py-1"
                        >{t("remove")}</button>
                      </div>
                    )}
                    <div className="mt-4" />
                  </>
                )}
                <div data-testid="parametro-section">
                  <FieldLabel>{t("parameter")}</FieldLabel>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <ParamBtn testId="param-distance-btn" active={paramMode === "distance"} onClick={() => setParamMode("distance")} label={t("kilometers")} />
                    <ParamBtn testId="param-time-btn" active={paramMode === "time"} onClick={() => setParamMode("time")} label={t("time")} />
                  </div>
                  {paramMode === "distance" ? (
                    <Slider testId="distance-slider" label={t("distance")} value={distance} min={5} max={200} unit="km" onChange={setDistance} color="yellow" />
                  ) : (
                    <Slider testId="time-slider" label={t("duration")} value={timeH} min={0.5} max={8} step={0.5} unit="h" onChange={setTimeH} color="yellow" />
                  )}
                </div>

                <TerrainSection t={t} terrain={terrain} onSelect={(k) => { setTerrain(k); setOffroad(RIDING_OFFROAD[k]); }} />
              </div>
            )}

            {/* Destination panel */}
            {activeTool === "destination" && (
              <div className="mt-4" data-testid="destination-panel">
                <FieldLabel>Destinazione</FieldLabel>
                <div className="mt-2 relative">
                  <input
                    data-testid="dest-search-input"
                    type="text"
                    value={destSearchQ}
                    onChange={(e) => setDestSearchQ(e.target.value)}
                    placeholder="Indirizzo, frazione, via, città…"
                    className="w-full bg-brema-obsidian border border-white/10 px-3 py-2 text-sm font-mono text-white focus:border-brema-yellow outline-none"
                  />
                  {destSearchQ.length < 2 && recentDests.length > 0 && !dest && (
                    <div data-testid="dest-recents-dropdown" className="mt-1 border border-white/10 bg-brema-obsidian max-h-56 overflow-y-auto">
                      <div className="flex items-center justify-between px-3 py-1.5 border-b border-white/10 bg-brema-coal">
                        <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-300">
                          {lang === "en" ? "Recent destinations" : "Destinazioni recenti"}
                        </span>
                        <button
                          data-testid="dest-recents-clear"
                          onClick={clearRecents}
                          className="font-mono text-[9px] uppercase tracking-widest text-zinc-400 hover:text-brema-red"
                        >
                          {lang === "en" ? "Clear" : "Cancella"}
                        </button>
                      </div>
                      {recentDests.map((r, idx) => (
                        <button
                          key={`${r.lat}-${r.lng}-${idx}`}
                          data-testid={`dest-recent-${idx}`}
                          onClick={() => applyDestPick(r.name, r.lat, r.lng)}
                          className="w-full text-left px-3 py-2 text-xs text-zinc-100 hover:bg-brema-yellow/10 hover:text-brema-yellow border-b border-white/5 last:border-b-0 flex items-center gap-2"
                        >
                          <span className="text-brema-yellow text-[10px]">◐</span>
                          <span className="truncate">{r.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {destSearchQ.length >= 2 && (
                    <div data-testid="dest-autocomplete-dropdown" className="mt-1 border border-white/10 bg-brema-obsidian max-h-56 overflow-y-auto">
                      {destSearching && (
                        <div className="px-3 py-2 font-mono text-[10px] text-zinc-400 uppercase tracking-widest">Ricerca…</div>
                      )}
                      {!destSearching && destSearchError && (
                        <div data-testid="dest-search-error" className="px-3 py-2 font-mono text-[10px] text-brema-red uppercase tracking-widest">{t("searchUnavailable")}</div>
                      )}
                      {!destSearching && !destSearchError && destSearchResults.length === 0 && (
                        <div className="px-3 py-2 font-mono text-[10px] text-zinc-400 uppercase tracking-widest">Nessun risultato</div>
                      )}
                      {!destSearching &&
                        destSearchResults.map((r) => (
                          <button
                            key={r.place_id}
                            data-testid={`dest-result-${r.place_id}`}
                            onClick={() => applyDestPick(r.display_name.split(",").slice(0, 2).join(", "), parseFloat(r.lat), parseFloat(r.lon))}
                            className="w-full text-left px-3 py-2 text-xs text-zinc-100 hover:bg-brema-yellow/10 hover:text-brema-yellow border-b border-white/5 last:border-b-0"
                          >
                            {r.display_name}
                          </button>
                        ))}
                    </div>
                  )}
                </div>

                {dest && (
                  <div className="mt-2 font-mono text-[10px] text-brema-yellow uppercase tracking-widest truncate" data-testid="dest-selected-chip">
                    → {dest.name}
                  </div>
                )}

                <TerrainSection t={t} terrain={terrain} onSelect={(k) => { setTerrain(k); setOffroad(RIDING_OFFROAD[k]); }} />
              </div>
            )}

            {/* Community panel */}
            {activeTool === "community" && (
              <div className="mt-4" data-testid="community-panel">
                <FieldLabel>Zona</FieldLabel>
                <select
                  data-testid="zone-select"
                  value={zoneId || ""}
                  onChange={(e) => setZoneId(e.target.value || null)}
                  className="mt-2 w-full bg-brema-obsidian border border-white/10 px-3 py-2 text-sm font-mono text-white focus:border-brema-yellow outline-none uppercase tracking-widest"
                >
                  <option value="">— Scegli una zona —</option>
                  {ZONES.map((z) => (
                    <option key={z.id} value={z.id}>{z.name}</option>
                  ))}
                </select>

                {zoneId && (
                  <div className="mt-3">
                    <FieldLabel>Archivio giri</FieldLabel>
                    <div className="mt-2 border border-white/10 bg-brema-obsidian max-h-72 overflow-y-auto" data-testid="community-list">
                      {communityLoading && (
                        <div className="px-3 py-3 font-mono text-[10px] text-zinc-400 uppercase tracking-widest">Caricamento…</div>
                      )}
                      {!communityLoading && communityRides.length === 0 && (
                        <div className="px-3 py-3 font-mono text-[10px] text-zinc-400 uppercase tracking-widest">
                          Nessun giro archiviato in questa zona.
                          <div className="mt-1 text-zinc-500 normal-case tracking-normal">
                            Salva un percorso ad Anello per iniziare il tuo archivio.
                          </div>
                        </div>
                      )}
                      {!communityLoading &&
                        communityRides.map((r) => (
                          <button
                            key={r.route_id}
                            onClick={() => loadCommunityRide(r)}
                            data-testid={`community-ride-${r.route_id}`}
                            className="w-full text-left px-3 py-2 border-b border-white/5 last:border-b-0 hover:bg-brema-yellow/10 transition"
                          >
                            <div className="font-mono text-xs text-brema-yellow uppercase tracking-wider truncate">
                              {r.name || r.start_label}
                            </div>
                            <div className="mt-1 font-mono text-[10px] text-zinc-300">
                              {r.distance_km} km · {r.difficulty} · {r.vehicle === "moto" ? "Moto" : "Bike"}
                            </div>
                          </button>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {!routeInSync && route && (
              <div className="mt-3 text-[10px] font-mono text-brema-yellow/80 uppercase tracking-widest">
                ⚠ Anteprima aggiornata — premi Generate per rigenerare
              </div>
            )}

            {/* Parametro section removed — Distanza/Durata sliders now live
                at the top of the loop/A-R panel (Track Following layout). */}

            <button
              onClick={startVoiceCommand}
              data-testid="voice-command-btn"
              className={`mt-5 w-full font-display font-bold uppercase tracking-widest px-4 py-3 flex items-center justify-center gap-3 transition-all text-sm ${
                voiceStage === "listening"
                  ? "bg-brema-red text-white animate-pulse"
                  : voiceStage === "processing"
                  ? "bg-brema-yellow/50 text-black cursor-wait"
                  : "bg-brema-yellow text-black hover:bg-amber-400"
              }`}
            >
              {voiceStage === "listening" || voiceStage === "awaiting_slot" || voiceStage === "awaiting_confirm" ? (
                <MicOff size={16} />
              ) : (
                <Mic size={16} />
              )}
              {voiceStage === "processing"
                ? "Elaboro…"
                : voiceStage === "listening"
                ? (lang === "en" ? "Listening… (tap to stop)" : "Sto ascoltando… (tap per fermare)")
                : voiceStage === "awaiting_slot"
                ? (lang === "en" ? "Waiting for your reply…" : "Attendo la tua risposta…")
                : voiceStage === "awaiting_confirm"
                ? (lang === "en" ? "Awaiting confirmation (say 'yes' or tap to exit)" : "In attesa di conferma (dì 'sì' o tap per uscire)")
                : (lang === "en" ? "Set by voice" : "Imposta con il vocale")}
            </button>
            {(voiceTranscript || voiceHint) && (
              <div className="mt-2 space-y-1" data-testid="voice-panel">
                {voiceTranscript && (
                  <div data-testid="voice-transcript" className="font-mono text-[11px] text-brema-yellow uppercase tracking-widest break-words">
                    “{voiceTranscript}”
                  </div>
                )}
                {voiceHint && (
                  <div data-testid="voice-hint" className="font-mono text-[10px] text-zinc-300 normal-case tracking-normal break-words">
                    {voiceHint}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4">
          {/* MAIN RESULTS CARD */}
          {activeTool !== "community" && (
            <div className="relative bg-brema-coal border border-white/10 p-6" data-testid="results-block">
              <span className="corner-tick tl" /><span className="corner-tick tr" />
              <span className="corner-tick bl" /><span className="corner-tick br" />
              <button
                onClick={generate}
                disabled={loading || (activeTool === "destination" && !dest)}
                data-testid="generate-route-btn"
                className="w-full bg-[#2ecc71] hover:bg-[#27b862] disabled:opacity-60 disabled:cursor-wait text-white font-display font-black uppercase tracking-widest px-4 py-4 flex items-center justify-center gap-2.5 transition-colors text-base"
              >
                {loading ? (
                  <><Loader2 size={16} className="animate-spin" />Calcolo in corso…</>
                ) : (
                  <>
                    <Play size={16} />
                    {activeTool === "destination"
                      ? t("computePath")
                      : activeTool === "andata-ritorno"
                      ? t("generateAR")
                      : t("generatePercorso")}
                  </>
                )}
              </button>
              <div className="mt-5 flex justify-center">
                <RingConfigurator
                  distance={displayDistance}
                  offroad={offroad}
                  vehicle={vehicle}
                  duration={displayDuration}
                  mode={paramMode}
                  timeH={timeH}
                  loading={loading}
                />
              </div>
              <div className="mt-5 grid grid-cols-3 gap-2" data-testid="stat-band">
                <StatCompact
                  icon={<Route size={13} />}
                  label={t("distance")}
                  value={typeof displayDistance === "number" ? displayDistance.toFixed(1) : displayDistance}
                  unit="KM"
                />
                <StatCompact
                  icon={<Timer size={13} />}
                  label={t("duration")}
                  value={`${Math.floor(displayDuration / 60)}h ${Math.round(displayDuration % 60)}m`}
                  unit=""
                />
                <StatCompact
                  testId="stat-offroad-km"
                  icon={<Route size={13} />}
                  label={t("offRoadKm")}
                  value={
                    (typeof displayDistance === "number"
                      ? displayDistance * (routeInSync ? (route?.offroad_percent ?? offroad) : offroad) / 100
                      : 0
                    ).toFixed(1)
                  }
                  unit="KM"
                />
              </div>
            </div>
          )}

          {/* Utility action row */}
          {route && (
            <div className="grid grid-cols-3 gap-2" data-testid="utility-actions">
              <button onClick={saveRoute} disabled={saving} data-testid="save-route-btn"
                className="bg-brema-coal border border-brema-yellow text-brema-yellow hover:bg-brema-yellow/10 disabled:opacity-40 font-display font-bold uppercase tracking-widest px-2 py-3 flex items-center justify-center gap-1.5 transition-colors text-xs">
                <Save size={14} />{saving ? "…" : t("save")}
              </button>
              <button onClick={shareRoute} disabled={sharing} data-testid="share-route-btn"
                title="Condividi il percorso su WhatsApp"
                className="bg-brema-coal border border-brema-yellow text-brema-yellow hover:bg-brema-yellow/10 disabled:opacity-40 font-display font-bold uppercase tracking-widest px-2 py-2 flex items-center justify-center gap-1.5 transition-colors text-[10px] leading-tight">
                <Share2 size={14} className="flex-shrink-0" />
                <span className="text-center">{sharing ? "…" : (lang === "en" ? "Share WhatsApp" : "Condividi WhatsApp")}</span>
              </button>
              <button onClick={downloadGpx} disabled={gpxDownloading} data-testid="gpx-download-btn"
                title="Scarica GPX per Garmin/Wahoo"
                className="bg-brema-coal border border-brema-yellow text-brema-yellow hover:bg-brema-yellow/10 disabled:opacity-40 font-display font-bold uppercase tracking-widest px-2 py-3 flex items-center justify-center gap-1.5 transition-colors text-xs">
                <Download size={14} />{gpxDownloading ? "…" : "GPX"}
              </button>
            </div>
          )}

          {/* Attrezzati con Brema */}
          {route && (
            <a
              href={vehicle === "moto" ? "https://www.brema-1969.com/e-shop/moto/" : "https://www.brema-1969.com/e-shop/bike/"}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="brema-gear-banner"
              className="relative overflow-hidden block bg-[#707372] hover:bg-[#7d8083] transition-colors p-3 group"
            >
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 w-9 h-9 border border-white/40 flex items-center justify-center">
                  <span className="font-display font-black text-white text-lg">B</span>
                </div>
                <div className="flex-1 min-w-0 text-center">
                  <div className="font-display font-bold uppercase tracking-widest text-white text-xs sm:text-sm">
                    Attrezzati con Brema
                  </div>
                </div>
                <ArrowRight size={16} className="text-white/80 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
              </div>
            </a>
          )}

          {/* AVVIA PERCORSO */}
          {route && (
            <div className="space-y-2">
              <button
                data-testid="live-tracking-btn"
                onClick={tracking ? () => stopTracking('user') : startTracking}
                className={`w-full px-4 py-5 border-2 font-display font-bold uppercase tracking-widest text-base flex items-center justify-center gap-3 transition-all ${
                  tracking
                    ? "bg-brema-red text-white border-brema-red hover:bg-brema-red/80"
                    : "bg-brema-red text-white border-brema-red hover:bg-[#D90429]"
                }`}
              >
                {tracking ? <Square size={18} /> : <Navigation size={18} />}
                {tracking ? (lang === "en" ? "STOP ROUTE" : "STOP PERCORSO") : (lang === "en" ? "START ROUTE" : "AVVIA PERCORSO")}
              </button>
              {tracking && (
                <div className="grid grid-cols-2 gap-2">
                  <StatCompact icon={<Route size={13} />} label="Percorsi" value={trackStats.km.toFixed(2)} unit="KM" accent="red" />
                  <StatCompact
                    icon={<Timer size={13} />}
                    label={movingNow ? "Tempo" : "Pausa"}
                    value={`${Math.floor(trackStats.seconds / 60)}:${String(trackStats.seconds % 60).padStart(2, "0")}`}
                    unit=""
                    accent={movingNow ? "white" : "yellow"}
                  />
                </div>
              )}
              {tracking && (
                <div className="grid grid-cols-2 gap-2">
                  <button
                    data-testid="compass-toggle-btn"
                    onClick={() => setCompassMode((v) => !v)}
                    className={`px-3 py-2 border font-mono uppercase tracking-widest text-[11px] flex items-center justify-center gap-2 transition-all ${
                      compassMode
                        ? "bg-brema-yellow text-brema-obsidian border-brema-yellow"
                        : "bg-transparent border-brema-yellow/40 text-brema-yellow hover:bg-brema-yellow/10"
                    }`}
                  >
                    <Compass size={12} />
                    {compassMode ? "COMPASS ON" : "COMPASS OFF"}
                  </button>
                  <button
                    data-testid="voice-toggle-btn"
                    onClick={() => {
                      const next = !voiceOn;
                      setVoiceOn(next);
                      if (!next && typeof window !== "undefined" && window.speechSynthesis) {
                        window.speechSynthesis.cancel();
                      }
                    }}
                    className={`px-3 py-2 border font-mono uppercase tracking-widest text-[11px] flex items-center justify-center gap-2 transition-all ${
                      voiceOn
                        ? "bg-brema-yellow text-brema-obsidian border-brema-yellow"
                        : "bg-transparent border-brema-yellow/40 text-brema-yellow hover:bg-brema-yellow/10"
                    }`}
                  >
                    {voiceOn ? <Volume2 size={12} /> : <VolumeX size={12} />}
                    {voiceOn ? "VOCE ON" : "VOCE OFF"}
                  </button>
                </div>
              )}
              {tracking && (
                <div
                  data-testid="tracking-voice-hint"
                  className="border border-brema-yellow/25 bg-brema-yellow/5 px-3 py-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-brema-yellow/85"
                  title={t("trackVoiceHelp")}
                >
                  <Mic size={11} className="animate-pulse" />
                  <span className="truncate">{t("trackVoiceHelp")}</span>
                </div>
              )}
            </div>
          )}

          <div className="relative bg-brema-coal border border-white/10 h-[520px] overflow-hidden">
            <span className="corner-tick tl" /><span className="corner-tick tr" />
            <span className="corner-tick bl" /><span className="corner-tick br" />
            {/* View toggle pill: Map ↔ Roadbook. Visible both idle AND
                during tracking so the rider can flip to the giant
                auto-advancing card without touching the config. */}
            {route && (
              <div
                data-testid="view-toggle"
                className="absolute top-3 right-3 z-[600] bg-brema-obsidian/85 border border-white/15 backdrop-blur flex"
              >
                <button
                  data-testid="view-toggle-map"
                  onClick={() => setVisualView("map")}
                  className={`px-3 py-1.5 font-mono uppercase tracking-widest text-[10px] transition ${
                    visualView === "map"
                      ? "bg-brema-yellow text-brema-obsidian"
                      : "text-brema-yellow hover:bg-brema-yellow/10"
                  }`}
                >
                  {lang === "en" ? "Map" : "Mappa"}
                </button>
                <button
                  data-testid="view-toggle-roadbook"
                  onClick={() => setVisualView("roadbook")}
                  className={`px-3 py-1.5 font-mono uppercase tracking-widest text-[10px] transition ${
                    visualView === "roadbook"
                      ? "bg-brema-yellow text-brema-obsidian"
                      : "text-brema-yellow hover:bg-brema-yellow/10"
                  }`}
                >
                  Roadbook
                </button>
              </div>
            )}
            {tracking ? (
              // While tracking, the map lives inside the fullscreen overlay
              // above. Render a compact placeholder here so the layout
              // doesn't jump when the user stops.
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="font-mono text-[10px] text-brema-yellow uppercase tracking-[0.3em] mb-2">
                    // Live tracking
                  </div>
                  <div className="font-display font-black text-white text-2xl uppercase">
                    {lang === "en" ? "Riding..." : "In marcia..."}
                  </div>
                  <div className="mt-4 text-zinc-400 text-xs font-mono uppercase tracking-widest">
                    {lang === "en" ? "Fullscreen map active" : "Mappa a tutto schermo attiva"}
                  </div>
                </div>
              </div>
            ) : visualView === "roadbook" && route ? (
              <div className="absolute inset-0 overflow-auto">
                <RoadbookView
                  turns={turnPoints}
                  totalKm={route?.distance_km || 0}
                  driving={false}
                  currentTurnIdx={currentTurnIdx}
                  nearTurn={nearTurn}
                />
              </div>
            ) : (
              <MapView
                start={start}
                polyline={routePolyline}
                live={null}
                followUser={false}
                compassMode={false}
                nearTurn={null}
                snapHeading={snapHeading}
                rejoinPolyline={null}
                weatherPoints={weatherPoints}
                speedKmh={0}
                turnPoint={
                  route?.route_type === "andata-ritorno" && route?.waypoints?.[1]
                    ? {
                        lat: route.waypoints[1][0],
                        lng: route.waypoints[1][1],
                        label: `${t("turnLabel")} · ${((route.distance_km || 0) / 2).toFixed(1)} ${t("kmShort")}${arTurn ? " · " + arTurn.name : ""}`,
                      }
                    : null
                }
                onPickStart={(lat, lng) => {
                  setStart({ name: lang === "en" ? "Custom pin" : "Pin personalizzato", lat, lng });
                  setStartFromGps(false);
                }}
              />
            )}
          </div>

          {/* Elevation profile chart */}
          {route?.elevation_profile?.length > 1 && (
            <ElevationProfile
              profile={route.elevation_profile}
              min_elev_m={route.min_elev_m}
              max_elev_m={route.max_elev_m}
              elevation_gain_m={route.elevation_gain_m}
              elevation_loss_m={route.elevation_loss_m}
            />
          )}
        </div>
      </main>
    </div>
  );
}

// ==================== Presentational sub-components ====================

function ElevationMiniStrip({ profile, minM, maxM, progressKm, totalKm }) {
  // Compact 40px-tall horizontal chart: draws the elevation as a filled
  // yellow area, then a red vertical bar at the rider's current position.
  const w = 100; // viewBox is percentage-based → responsive to container
  const h = 40;
  const min = minM ?? Math.min(...profile.map((p) => p.elev));
  const max = maxM ?? Math.max(...profile.map((p) => p.elev));
  const range = Math.max(1, max - min);
  const path = profile
    .map((p, i) => {
      const x = (i / (profile.length - 1)) * w;
      const y = h - ((p.elev - min) / range) * (h - 4) - 2;
      return `${i === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(" ");
  const fill = `${path} L ${w} ${h} L 0 ${h} Z`;
  const progressX = totalKm > 0 ? Math.min(1, Math.max(0, progressKm / totalKm)) * w : 0;
  const currentElev = (() => {
    if (!totalKm) return null;
    const t = Math.min(1, Math.max(0, progressKm / totalKm));
    const idxF = t * (profile.length - 1);
    const i0 = Math.floor(idxF);
    const i1 = Math.min(profile.length - 1, i0 + 1);
    const frac = idxF - i0;
    return Math.round(profile[i0].elev * (1 - frac) + profile[i1].elev * frac);
  })();
  return (
    <div className="relative">
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full h-10 block">
        <path d={fill} fill="#FFD400" fillOpacity="0.25" />
        <path d={path} fill="none" stroke="#FFD400" strokeWidth="1.2" />
        <line
          x1={progressX}
          x2={progressX}
          y1={0}
          y2={h}
          stroke="#C1272D"
          strokeWidth="1.5"
        />
      </svg>
      <div className="flex justify-between mt-0.5 font-mono text-[9px] tabular-nums text-zinc-400">
        <span>{Math.round(min)} m</span>
        {currentElev != null && (
          <span className="text-brema-red">▲ {currentElev} m</span>
        )}
        <span>{Math.round(max)} m</span>
      </div>
    </div>
  );
}

function StatCompact({ icon, label, value, unit, accent, testId }) {
  const valueColor =
    accent === "red" ? "text-brema-red" :
    accent === "yellow" ? "text-brema-yellow" :
    "text-white";
  return (
    <div data-testid={testId} className="bg-brema-obsidian/50 border border-white/10 px-3 py-2">
      <div className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-zinc-400">
        <span className="text-zinc-300">{icon}</span>{label}
      </div>
      <div className={`mt-1 font-display font-bold text-lg leading-none ${valueColor}`}>
        {value}<span className="ml-1 text-[10px] font-mono text-zinc-400">{unit}</span>
      </div>
    </div>
  );
}

function FieldLabel({ children }) {
  return <div className="font-mono text-xs font-normal text-zinc-200 uppercase tracking-[0.2em]">{children}</div>;
}
function VehicleBtn({ active, onClick, icon, label, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`relative border px-2 py-3 flex flex-col items-center gap-1 uppercase font-display tracking-wider text-[11px] transition-all ${
        active ? "border-brema-yellow bg-brema-yellow/10 text-brema-yellow" : "border-white/10 text-zinc-100 hover:border-white/30"
      }`}>
      {icon}<span className="font-bold text-center leading-tight">{label}</span>
    </button>
  );
}
function ToolBtn({ active, onClick, icon, label, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`relative border px-2 h-[84px] flex flex-col items-center justify-center gap-1.5 uppercase font-mono tracking-widest text-[11px] transition-all ${
        active
          ? "border-brema-yellow bg-brema-yellow/10 text-brema-yellow"
          : "border-white/10 text-zinc-100 hover:border-brema-yellow/40"
      }`}
    >
      <span>{icon}</span>
      <span className="text-center leading-tight break-words px-1">{label}</span>
    </button>
  );
}
function ParamBtn({ active, onClick, label, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`text-xs px-3 py-2 font-mono uppercase tracking-widest border transition ${
        active ? "border-brema-yellow text-brema-yellow" : "border-white/10 text-zinc-200 hover:text-zinc-300"
      }`}>{label}</button>
  );
}
function TerrainSection({ t, terrain, onSelect }) {
  const items = [
    { key: "turismo", label: t("terrainTurismo"), desc: t("terrainTurismoDesc"), icon: <Route size={16} /> },
    { key: "sterrato", label: t("terrainSterrato"), desc: t("terrainSterratoDesc"), icon: <Mountain size={16} /> },
    { key: "adventure", label: t("terrainAdventure"), desc: t("terrainAdventureDesc"), icon: <Waypoints size={16} /> },
    { key: "enduro", label: t("terrainEnduro"), desc: t("terrainEnduroDesc"), icon: <TreePine size={16} /> },
  ];
  return (
    <div className="mt-5">
      <FieldLabel>
        {t("terrain")}{" "}
        <span className="text-zinc-400 lowercase tracking-normal font-mono text-[10px]">{t("terrainPrefNote")}</span>
      </FieldLabel>
      <div className="grid grid-cols-2 gap-2 mt-2" data-testid="terrain-cards">
        {items.map((it) => (
          <button
            key={it.key}
            data-testid={`terrain-${it.key}-btn`}
            onClick={() => onSelect(it.key)}
            className={`border px-3 py-3 flex items-start gap-2.5 text-left transition-all ${
              terrain === it.key
                ? "border-brema-yellow bg-brema-yellow/10"
                : "border-white/10 hover:border-brema-yellow/40"
            }`}
          >
            <span className={`mt-0.5 shrink-0 ${terrain === it.key ? "text-brema-yellow" : "text-zinc-400"}`}>{it.icon}</span>
            <span className="min-w-0">
              <span className={`block font-display font-bold uppercase tracking-wider text-[12px] ${terrain === it.key ? "text-brema-yellow" : "text-zinc-100"}`}>
                {it.label}
              </span>
              <span className="block mt-0.5 font-mono text-[10px] text-zinc-400 leading-snug">{it.desc}</span>
            </span>
          </button>
        ))}
      </div>
      <div data-testid="terrain-info-box" className="mt-3 border border-white/10 bg-brema-obsidian px-3 py-2.5 flex items-start gap-2">
        <Info size={13} className="text-zinc-400 mt-0.5 shrink-0" />
        <span className="font-mono text-[10px] text-zinc-300 leading-relaxed">{t("terrainInfo")}</span>
      </div>
    </div>
  );
}
function Slider({ label, value, min, max, step = 1, unit, onChange, color, testId }) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="mt-5">
      <div className="flex items-baseline justify-between">
        <FieldLabel>{label}</FieldLabel>
        <div className="font-mono text-brema-yellow font-bold">
          {value}<span className="text-zinc-200 ml-1 text-xs">{unit}</span>
        </div>
      </div>
      <input data-testid={testId} type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className={`brema-range ${color === "red" ? "brema-range-red" : ""} mt-2`}
        style={{ "--val": `${pct}%` }} />
    </div>
  );
}
